import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { TextMaskCustom } from 'components/TextMaskCustom/TextMaskCustom'
import { usePostVerifyTwoFAApp } from 'pages/MyAccountEdit/hooks/usePostVerifyTwoFAApp'

import { usePostGenerateCodeChallenge } from './hooks/usePostGenerateCodeChallenge'
import { QRCodeWithLogo } from './QRCodeWithLogo'
import { TwoFAType } from './types'

type Props = {
  qrCode: string | null
  onBack: () => void
  onSuccess: () => void
}

export const TwoFAVerifyApp = ({ qrCode, onBack, onSuccess }: Props) => {
  const [value, onChange] = useState('')
  const [step, setStep] = useState(1)
  const { data, isLoading: isPendingVerify } = usePostGenerateCodeChallenge(qrCode, TwoFAType.googleAuthenticator)
  const { mutateAsync: verify, isLoading: isPendingSetting } = usePostVerifyTwoFAApp(() => {
    onSuccess()
  })
  const { t } = useTranslation()

  if (!qrCode) {
    return null
  }

  const handleSave = async () => {
    await verify({
      code: String(value),
      code_challenge: data?.data?.code_challenge || '',
      type: TwoFAType.googleAuthenticator,
    })
  }

  return (
    <Box>
      <Collapse in={step === 1}>
        <Typography variant="h3" fontWeight="bold" align="center">
          {t('profile.two_factor_authentication.scan.title')}
        </Typography>
        <Typography variant="h3" fontWeight="bold" align="center" mt={2}>
          {t('profile.two_factor_authentication.scan')}
        </Typography>
        <Typography variant="body1" mt={2} align="center">
          {t('profile.two_factor_authentication.scan.info')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          {qrCode && <QRCodeWithLogo url={qrCode || ''} />}
        </Box>
        {data?.data?.secret && (
          <Box mt={1}>
            <Typography variant="body1" align="center">
              {t('profile.two_factor_authentication.enter_manually')}
            </Typography>
            <Typography variant="body1" align="center">
              {data?.data?.secret}
            </Typography>
          </Box>
        )}
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} maxWidth={300} sx={{ marginX: 'auto', mt: 2 }}>
          <Button variant="outlined" color="primary" onClick={onBack}>
            {t('app.back')}
          </Button>
          <Button variant="contained" color="primary" onClick={() => setStep(2)}>
            {t('app.next')}
          </Button>
        </Box>
      </Collapse>
      <Collapse in={step === 2}>
        <Typography variant="h3" fontWeight="bold" align="center">
          {t('profile.two_factor_authentication.enter_code')}
        </Typography>
        <Typography align="center">{t('profile.two_factor_authentication.description3')}</Typography>
        <Box my={2} px={{ xs: 2, lg: 16 }} gridTemplateColumns="1fr" display="grid" gap={2} maxWidth={600} mx={'auto'}>
          <TextField
            autoComplete="off"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            name="textmask"
            label={t('profile.two_factor_authentication.code')}
            slotProps={{ input: { inputComponent: TextMaskCustom as any, inputProps: { handleSubmit: handleSave } } }}
            size="small"
          />
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Button variant="outlined" color="primary" onClick={() => setStep(1)}>
              {t('app.back')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              loading={isPendingVerify || isPendingSetting}
            >
              {t('profile.two_factor_authentication.verify')}
            </Button>
          </Box>
        </Box>
        <Typography align="center">{t('profile.two_factor_authentication.description4')}</Typography>
      </Collapse>
    </Box>
  )
}
